export type KnownSite = {
  hostname: string;
  brand: string;
  defaultServerSlug: string;
};

const SERVER_SLUGS = ["frostreaver", "fangbreaker"] as const;
export type KnownServerSlug = (typeof SERVER_SLUGS)[number];

const SITE_BY_HOST: Record<string, KnownSite> = {
  "frostreaver.zone": {
    hostname: "frostreaver.zone",
    brand: "Frostreaver.zone",
    defaultServerSlug: "frostreaver",
  },
  "www.frostreaver.zone": {
    hostname: "www.frostreaver.zone",
    brand: "Frostreaver.zone",
    defaultServerSlug: "frostreaver",
  },
  "beta.frostreaver.zone": {
    hostname: "beta.frostreaver.zone",
    brand: "Frostreaver.zone",
    defaultServerSlug: "frostreaver",
  },
  "fangbreaker.zone": {
    hostname: "fangbreaker.zone",
    brand: "Fangbreaker.zone",
    defaultServerSlug: "fangbreaker",
  },
  "www.fangbreaker.zone": {
    hostname: "www.fangbreaker.zone",
    brand: "Fangbreaker.zone",
    defaultServerSlug: "fangbreaker",
  },
  "beta.fangbreaker.zone": {
    hostname: "beta.fangbreaker.zone",
    brand: "Fangbreaker.zone",
    defaultServerSlug: "fangbreaker",
  },
  localhost: {
    hostname: "localhost",
    brand: "Frostreaver.zone",
    defaultServerSlug: "frostreaver",
  },
  "127.0.0.1": {
    hostname: "127.0.0.1",
    brand: "Frostreaver.zone",
    defaultServerSlug: "frostreaver",
  },
};

const HOST_BY_SERVER: Record<KnownServerSlug, { live: string; beta: string }> =
  {
    frostreaver: { live: "frostreaver.zone", beta: "beta.frostreaver.zone" },
    fangbreaker: { live: "fangbreaker.zone", beta: "beta.fangbreaker.zone" },
  };

export function getSiteContext(
  hostname = typeof window !== "undefined"
    ? window.location.hostname.toLowerCase()
    : "",
): KnownSite {
  return (
    SITE_BY_HOST[hostname] ?? {
      hostname,
      brand: "Frostreaver.zone",
      defaultServerSlug: "frostreaver",
    }
  );
}

export function isKnownServerSlug(
  value: string | null | undefined,
): value is KnownServerSlug {
  return value === "frostreaver" || value === "fangbreaker";
}

export function getActiveServerSlug(_pathname?: string, hostname?: string) {
  return getSiteContext(hostname).defaultServerSlug;
}

export function getOtherServerSlug(serverSlug: string) {
  return serverSlug === "fangbreaker" ? "frostreaver" : "fangbreaker";
}

export function serverDisplayName(serverSlug: string) {
  return serverSlug === "fangbreaker" ? "Fangbreaker" : "Frostreaver";
}

function currentProtocol() {
  return typeof window !== "undefined" ? window.location.protocol : "https:";
}

function isBetaHost(
  hostname = typeof window !== "undefined"
    ? window.location.hostname.toLowerCase()
    : "",
) {
  return hostname.startsWith("beta.");
}

export function serverHost(serverSlug: string, hostname?: string) {
  const slug = isKnownServerSlug(serverSlug) ? serverSlug : "frostreaver";
  const mode = isBetaHost(hostname) ? "beta" : "live";
  return HOST_BY_SERVER[slug][mode];
}

export function serverUrl(serverSlug: string, path = "/", hostname?: string) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${currentProtocol()}//${serverHost(serverSlug, hostname)}${cleanPath}`;
}

function pathOnly(value: string) {
  const input = value || "/";
  return input.split(/[?#]/, 1)[0] || "/";
}

export function serverSwitchPath(pathname: string) {
  const path = pathOnly(pathname);

  if (/^\/reports\/[^/]+/.test(path)) {
    return "/reports";
  }

  if (/^\/encounters\/(?!recent\/?$)[^/]+/.test(path)) {
    return "/reports";
  }

  if (/^\/guilds\/[^/]+/.test(path)) {
    return "/guilds";
  }

  if (/^\/characters\/[^/]+/.test(path)) {
    return "/characters";
  }

  if (
    /^\/resource-hunter\/zones\/[^/]+/.test(path) ||
    /^\/bonuses\/zones\/[^/]+/.test(path)
  ) {
    return "/resource-hunter/zones";
  }

  return pathname || "/";
}

export function swapServerInPath(pathname: string, nextServerSlug: string) {
  return serverUrl(nextServerSlug, serverSwitchPath(pathname));
}

export function serverRankingsPath(_serverSlug?: string) {
  return "/rankings";
}

export function serverAllStarsPath(
  _serverSlug: string,
  params?: {
    expansionSlug?: string | null;
    partitionSlug?: string | null;
    bossSlug?: string | null;
    metric?: "DAMAGE" | "HEALING";
    all?: boolean;
    pageNum?: number;
    classSlug?: string | null;
  },
) {
  const expansionSlug = params?.expansionSlug || "classic";
  const partitionSlug = params?.partitionSlug || "default";
  const bossSlug = params?.bossSlug || "all";
  const category = params?.metric === "HEALING" ? "healing" : "damage";
  const base = `/rankings/allstars/${encodeURIComponent(expansionSlug)}/${encodeURIComponent(partitionSlug)}/${encodeURIComponent(bossSlug)}/${category}`;
  const full = params?.all
    ? `${base}/all/${encodeURIComponent(String(params.pageNum ?? 1))}`
    : base;
  return params?.classSlug ? `${full}/${encodeURIComponent(params.classSlug)}` : full;
}


export function serverBossRankingsPath(
  _serverSlug: string,
  expansionSlug = "classic",
) {
  return `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}`;
}

export function serverBossRankingMetricPath(
  _serverSlug: string,
  expansionSlug: string,
  bossSlug: string | number,
  metric: "DAMAGE" | "HEALING",
  classSlug?: string | null,
) {
  const segment = metric === "HEALING" ? "healing" : "damage";
  const base = `${serverBossRankingsPath(_serverSlug, expansionSlug)}/${encodeURIComponent(String(bossSlug))}/${segment}`;
  return classSlug ? `${base}/${encodeURIComponent(classSlug)}` : base;
}

export function serverBossProgressionPath(
  _serverSlug: string,
  bossIdOrSlug: string | number,
  expansionSlug?: string | null,
) {
  if (expansionSlug) {
    return `/rankings/progression/${encodeURIComponent(expansionSlug)}/${encodeURIComponent(String(bossIdOrSlug))}`;
  }

  return `/rankings/boss/${encodeURIComponent(String(bossIdOrSlug))}`;
}

export function serverBossMetricPath(
  _serverSlug: string,
  bossId: string | number,
  metric: "DAMAGE" | "HEALING",
  classSlug?: string | null,
) {
  const segment = metric === "HEALING" ? "healing" : "damage";
  const base = `/rankings/boss/${encodeURIComponent(String(bossId))}/${segment}`;
  return classSlug ? `${base}/${encodeURIComponent(classSlug)}` : base;
}

export function serverTierProgressionPath(
  _serverSlug: string,
  expansionSlug: string,
) {
  return `/rankings/progression/${encodeURIComponent(expansionSlug)}`;
}

export type SluggableRef =
  | string
  | number
  | {
      id?: string | number | null;
      slug?: string | null;
      name?: string | null;
      normalizedName?: string | null;
      characterName?: string | null;
    }
  | null
  | undefined;

export function slugSegment(value: SluggableRef) {
  if (value && typeof value === "object") {
    const explicit =
      value.slug ??
      value.normalizedName ??
      value.name ??
      value.characterName ??
      value.id;
    return encodeURIComponent(
      String(explicit ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9`'._-]+/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || String(value.id ?? ""),
    );
  }
  return encodeURIComponent(String(value ?? ""));
}

export function serverGuildsPath(_serverSlug?: string) {
  return "/guilds";
}

export function serverGuildPath(serverSlug: string, guild: SluggableRef) {
  return `${serverGuildsPath(serverSlug)}/${slugSegment(guild)}`;
}

export function serverGuildProgressionPath(
  serverSlug: string,
  guild: SluggableRef,
) {
  return `${serverGuildPath(serverSlug, guild)}/progression`;
}

export function serverCharacterPath(
  _serverSlug: string,
  character: SluggableRef,
) {
  return `/characters/${slugSegment(character)}`;
}

export function serverCharacterBossParsesPath(
  serverSlug: string,
  character: SluggableRef,
  boss: SluggableRef,
  metric: "DAMAGE" | "HEALING" = "DAMAGE",
) {
  const metricQuery = metric === "HEALING" ? "?metric=HEALING" : "";
  return `${serverCharacterPath(serverSlug, character)}/bosses/${slugSegment(boss)}${metricQuery}`;
}

export function serverReportsPath(_serverSlug?: string) {
  return "/reports";
}

export function serverReportPath(
  serverSlug: string,
  reportId: string | number,
) {
  return `${serverReportsPath(serverSlug)}/${encodeURIComponent(String(reportId))}`;
}
